import React, { Key } from 'react';
import {
  Table,
  Tag,
  type TableProps,
  Typography,
  type TableColumnProps,
  type TableColumnType,
  ConfigProvider,
  Empty,
} from 'antd';
import { DesktopOutlined, MobileOutlined } from '@ant-design/icons';
import { type SortOrder } from 'antd/es/table/interface';

import { CustomTooltip } from '../../components/Tooltip';
import { PointInTime, pointInTimeCell } from '../../components/PointInTime';
import { type PageCrawl } from './PageCrawl';
import { crawlerCategoryLabel, crawlerCategory, type CrawlerCategoryScheme } from '~features/crawler-filter';
import { useCrawlerCategoryScheme } from '~features/crawler-filter/CrawlerFilterHooks';
import { CDNAnalyticQueryParams, QueryParamNames } from './QueryParameters';
import StatusDropdownFilter, { type StatusCodeFilter } from '../../components/dropdownFilters/StatusDropdownFilter';
import ResponseDropdownFilter, {
  type ResponseTimeFilter,
} from '../../components/dropdownFilters/ResponseDropdownFilter';
import { LoadErrorEmptyState, WIDGET_LOAD_ERROR_HEADLINES } from '~features/empty-states';

export const PAGE_SIZE_DEFAULT = 30;

function getStatusCodeColor(statusCode: number): string {
  if (statusCode >= 200 && statusCode < 300) {
    return 'green';
  }
  if (statusCode >= 300 && statusCode < 400) {
    return 'yellow';
  }
  if (statusCode >= 400 && statusCode < 500) {
    return 'orange';
  }
  return 'red';
}

function formatResponseTime(ms: number): string {
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(ms % 1000 === 0 ? 0 : 1)} s`;
}

function columnsFor(categoryScheme: CrawlerCategoryScheme): TableColumnProps<PageCrawl>[] {
  return [
    {
      title: 'Crawler',
      dataIndex: 'crawler',
      sorter: true,
      sortOrder: null,
      render: (value, item) => {
        if (!item.user_agent) return value;
        return (
          <CustomTooltip message={item.user_agent} placement="right">
            {value}
          </CustomTooltip>
        );
      },
    },
    {
      title: 'Category',
      dataIndex: 'crawler',
      render: (value) => crawlerCategoryLabel(crawlerCategory(value, categoryScheme)),
    },
    {
      title: 'Status',
      dataIndex: 'status_code',
      align: 'center',
      sorter: true,
      sortOrder: null,
      filters: [],
      filteredValue: null,
      filterDropdown: ({ confirm, setSelectedKeys, selectedKeys, clearFilters }) => (
        <StatusDropdownFilter
          confirm={confirm}
          selectedKeys={selectedKeys as StatusCodeFilter[]}
          setSelectedKeys={setSelectedKeys as (keys: StatusCodeFilter[]) => void}
          clearFilters={clearFilters}
        />
      ),
      filterDropdownProps: { destroyOnHidden: true },
      render: (value) => <Tag color={getStatusCodeColor(value)}>{value}</Tag>,
    },
    {
      title: 'Cached',
      dataIndex: 'cache_hit',
      align: 'center',
      sorter: true,
      sortOrder: null,
      filters: [
        { text: 'Cache Hit', value: 1 },
        { text: 'Cache Miss', value: 0 },
      ],
      filteredValue: null,
      render: (value) => <Tag color={value ? 'green' : 'orange'}>{value ? 'Hit' : 'Miss'}</Tag>,
    },
    {
      title: 'Response',
      dataIndex: 'render_time',
      align: 'center',
      sorter: true,
      filteredValue: null,
      filterDropdown: ({ confirm, setSelectedKeys, selectedKeys, clearFilters }) => (
        <ResponseDropdownFilter
          confirm={confirm}
          selectedKeys={selectedKeys as ResponseTimeFilter[]}
          setSelectedKeys={setSelectedKeys as (keys: ResponseTimeFilter[]) => void}
          clearFilters={clearFilters}
          useMilliseconds
        />
      ),
      filterDropdownProps: { destroyOnHidden: true },
      render: (value) => formatResponseTime(value),
    },
    {
      title: 'URL',
      dataIndex: 'url',
      sorter: true,
      sortOrder: null,
      minWidth: 300,
      render: (url) => (
        <Typography.Link href={url} target="_blank" rel="noreferrer">
          {url}
        </Typography.Link>
      ),
    },
    {
      title: 'Device',
      dataIndex: 'adaptive_type',
      align: 'center',
      sorter: true,
      sortOrder: null,
      filters: [
        { text: 'Desktop', value: 'desktop' },
        { text: 'Mobile', value: 'mobile' },
      ],
      filteredValue: null,
      render: (value) => (
        <CustomTooltip
          message={`Rendering was optimized for ${value === 'mobile' ? 'Mobile' : 'Desktop'} crawlers`}
          placement="left"
        >
          {value === 'mobile' ? (
            <MobileOutlined style={{ fontSize: 16 }} />
          ) : (
            <DesktopOutlined style={{ fontSize: 16 }} />
          )}
        </CustomTooltip>
      ),
    },
    {
      title: 'Processed',
      dataIndex: 'time',
      sorter: true,
      sortOrder: null,
      filteredValue: undefined,
      onCell: pointInTimeCell,
      render: (value) => <PointInTime value={value} />,
    },
  ];
}

type CDNAnalyticsTableProps = {
  updateFilters: (newFilters: Partial<CDNAnalyticQueryParams>) => void;
  updatePagination: (page: number, pageSize: number) => void;
  queryParameters: Partial<CDNAnalyticQueryParams>;
  data?: PageCrawl[];
  totalCount: number;
  isLoading?: boolean;
  isError?: boolean;
  onRetry: () => void;
  emptyRows: React.ReactNode;
};

function CDNAnalyticsTable({
  updateFilters,
  updatePagination,
  queryParameters,
  data = [],
  totalCount,
  isLoading = false,
  isError = false,
  onRetry,
  emptyRows,
}: CDNAnalyticsTableProps) {
  const categoryScheme = useCrawlerCategoryScheme();
  const enhancedColumns = React.useMemo(
    () =>
      columnsFor(categoryScheme).map((column) => {
        const additionalProps: TableColumnType<PageCrawl> = {};
        const isSortable = column.sorter;
        const { [QueryParamNames.Sort]: sort, [QueryParamNames.SortDirection]: sortDirection } = queryParameters;

        if (sort && sortDirection && isSortable && sort === column.dataIndex) {
          additionalProps.sortOrder = sortDirection === 'ASC' ? 'ascend' : ('descend' as SortOrder);
        }

        if (column.dataIndex === 'adaptive_type' && queryParameters[QueryParamNames.AdaptiveType]) {
          additionalProps.filteredValue = queryParameters[QueryParamNames.AdaptiveType];
        }

        if (column.dataIndex === 'cache_hit' && queryParameters[QueryParamNames.CacheHit]) {
          additionalProps.filteredValue = queryParameters[QueryParamNames.CacheHit];
        }

        if (
          column.dataIndex === 'status_code' &&
          (queryParameters[QueryParamNames.StatusCode] ||
            queryParameters[QueryParamNames.StatusCodeLow] ||
            queryParameters[QueryParamNames.StatusCodeHigh])
        ) {
          additionalProps.filteredValue = [
            {
              statusCodeEq: queryParameters[QueryParamNames.StatusCode],
              statusCodeLow: queryParameters[QueryParamNames.StatusCodeLow],
              statusCodeHigh: queryParameters[QueryParamNames.StatusCodeHigh],
            } as unknown as Key, // AntD expect a strict structure like value: boolean, we need more complex filtering here
          ];
        }

        if (
          column.dataIndex === 'render_time' &&
          (queryParameters[QueryParamNames.ResponseTimeLow] ||
            queryParameters[QueryParamNames.ResponseTimeHigh] ||
            queryParameters[QueryParamNames.Timedout])
        ) {
          additionalProps.filteredValue = [
            {
              responseTimeLow: queryParameters[QueryParamNames.ResponseTimeLow],
              responseTimeHigh: queryParameters[QueryParamNames.ResponseTimeHigh],
              timedout: queryParameters[QueryParamNames.Timedout],
            } as unknown as Key, // AntD expect a strict structure like value: boolean, we need more complex filtering here
          ];
        }

        return { ...column, ...additionalProps };
      }),
    [queryParameters, categoryScheme]
  );

  const onChange: TableProps<PageCrawl>['onChange'] = (_pagination, filters, rawSorter, extra) => {
    // pagination handled in its own callback
    if (extra.action === 'paginate') {
      return;
    }
    const sorter = Array.isArray(rawSorter) ? rawSorter[0] : rawSorter;

    const newFilters: Partial<CDNAnalyticQueryParams> = {
      page: undefined,
      page_size: queryParameters.page_size,
    };

    // SORTING
    newFilters.sort = sorter.field as CDNAnalyticQueryParams['sort'] | undefined;
    newFilters.sort_direction = sorter.order === 'ascend' ? 'ASC' : sorter.order === 'descend' ? 'DESC' : undefined;

    // FILTERS
    newFilters.adaptive_type = filters.adaptive_type
      ? (filters.adaptive_type as CDNAnalyticQueryParams['adaptive_type'])
      : undefined;
    newFilters.cache_hit = filters.cache_hit ? (filters.cache_hit as CDNAnalyticQueryParams['cache_hit']) : undefined;

    const statusCodeFilter = filters.status_code as StatusCodeFilter[] | undefined;
    newFilters.status_code = statusCodeFilter?.length ? statusCodeFilter[0].statusCodeEq : undefined;
    newFilters.status_code_low = statusCodeFilter?.length ? statusCodeFilter[0].statusCodeLow : undefined;
    newFilters.status_code_high = statusCodeFilter?.length ? statusCodeFilter[0].statusCodeHigh : undefined;

    const responseTimeFilter = filters.render_time as ResponseTimeFilter[] | undefined;
    newFilters.response_time_low = responseTimeFilter?.length ? responseTimeFilter[0].responseTimeLow : undefined;
    newFilters.response_time_high = responseTimeFilter?.length ? responseTimeFilter[0].responseTimeHigh : undefined;
    newFilters.timedout = responseTimeFilter?.length ? responseTimeFilter[0].timedout : undefined;

    updateFilters(newFilters);
  };

  const tableFallback = () => {
    if (isLoading) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Loading crawler requests" />;
    if (isError)
      return (
        <LoadErrorEmptyState
          screen="cdn_analytics"
          headline={WIDGET_LOAD_ERROR_HEADLINES.crawlerRequests}
          onRetry={onRetry}
          compact
        />
      );
    return emptyRows;
  };

  return (
    <ConfigProvider
      renderEmpty={(componentName) => {
        if (componentName !== 'Table') {
          return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Empty" />;
        }
        return tableFallback();
      }}
    >
      <Table<PageCrawl>
        loading={isLoading}
        rowKey="id"
        columns={enhancedColumns}
        dataSource={data}
        onChange={onChange}
        scroll={{ x: true }}
        tableLayout="auto"
        bordered
        size="middle"
        pagination={{
          onChange: (page, pageSize) => {
            const currentPageSize = queryParameters.page_size || PAGE_SIZE_DEFAULT;
            if (pageSize !== currentPageSize) updatePagination(1, pageSize);
            else updatePagination(page, pageSize);
          },
          current: queryParameters.page || 1,
          pageSize: queryParameters.page_size || PAGE_SIZE_DEFAULT,
          total: totalCount,
          showSizeChanger: {
            showSearch: false,
          },
          pageSizeOptions: ['10', '20', '30', '50', '100'],
        }}
      />
    </ConfigProvider>
  );
}

export default CDNAnalyticsTable;
